import api from "../api";
import {
  SFTPConnection,
  SFTPConnectionsResponse,
  SFTPConnectionCreatePayload,
  SFTPConnectionUpdatePayload,
  SFTPPublicKey,
} from "types/interfaces/sftp";

const BASE = "/api/sftp/connections";

export interface GenerateEncryptedSftpKeyPairPayload {
  algorithm: "ed25519" | "rsa-4096";
  passphrase: string;
}

export interface GenerateEncryptedSftpKeyPairResponse {
  private_key: string;
  public_key: string;
}

export interface SftpPublicConfig {
  public_hostname: string;
  default_server_id: string;
}

interface PaginatedSFTPConnections {
  count: number;
  next: string | null;
  previous: string | null;
  results: SFTPConnection[];
}

const sftpApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getSftpPublicConfig: builder.query<SftpPublicConfig, void>({
      query: () => "/api/sftp/public-config/",
    }),

    getSftpConnections: builder.query<SFTPConnection[], void>({
      query: () => `${BASE}/`,
      transformResponse: (response: PaginatedSFTPConnections | SFTPConnection[]) =>
        Array.isArray(response) ? response : response.results,
      providesTags: ["SftpConnections"],
    }),

    getSftpConnection: builder.query<SFTPConnection, number>({
      query: (id) => `${BASE}/${id}/`,
      providesTags: (_result, _error, id) => [{ type: "SftpConnections", id }],
    }),

    createSftpConnection: builder.mutation<SFTPConnection, SFTPConnectionCreatePayload>({
      query: (body) => ({
        url: `${BASE}/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["SftpConnections"],
    }),

    updateSftpConnection: builder.mutation<SFTPConnection, SFTPConnectionUpdatePayload>({
      query: ({ id, ...body }) => ({
        url: `${BASE}/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "SftpConnections",
        { type: "SftpConnections", id },
      ],
    }),

    deleteSftpConnection: builder.mutation<void, number>({
      query: (id) => ({
        url: `${BASE}/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["SftpConnections"],
    }),

    addSftpPublicKey: builder.mutation<SFTPPublicKey, { connectionId: number; public_key: string }>({
      query: ({ connectionId, public_key }) => ({
        url: `${BASE}/${connectionId}/add_key/`,
        method: "POST",
        body: { public_key },
      }),
      invalidatesTags: (_result, _error, { connectionId }) => [
        { type: "SftpConnections", id: connectionId },
        "SftpConnections",
      ],
    }),

    removeSftpPublicKey: builder.mutation<void, { connectionId: number; keyId: number }>({
      query: ({ connectionId, keyId }) => ({
        url: `${BASE}/${connectionId}/remove_key/`,
        method: "DELETE",
        body: { key_id: keyId },
      }),
      invalidatesTags: (_result, _error, { connectionId }) => [
        { type: "SftpConnections", id: connectionId },
        "SftpConnections",
      ],
    }),

    getBackendUserSftpConnections: builder.query<SFTPConnectionsResponse, number>({
      query: (userId) => `/api/admin/users/${userId}/sftp-connections/`,
      providesTags: (_result, _error, userId) => [
        { type: "SftpConnections", id: `user-${userId}` },
      ],
    }),

    revealSftpPassword: builder.query<{ password: string }, number>({
      query: (id) => `${BASE}/${id}/reveal_password/`,
    }),

    checkSftpUsername: builder.query<
      { exists: boolean },
      { username: string; exclude_id?: number; server_id?: string }
    >({
      query: ({ username, exclude_id, server_id }) => {
        const params = new URLSearchParams({ username });
        if (exclude_id) params.set("exclude_id", String(exclude_id));
        if (server_id) params.set("server_id", server_id);
        return `${BASE}/check_username/?${params.toString()}`;
      },
    }),

    generateEncryptedSftpKeyPair: builder.mutation<
      GenerateEncryptedSftpKeyPairResponse,
      GenerateEncryptedSftpKeyPairPayload
    >({
      query: (body) => ({
        url: "/api/sftp/helper/generate-encrypted-keypair/",
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useGetSftpPublicConfigQuery,
  useGetSftpConnectionsQuery,
  useGetSftpConnectionQuery,
  useCreateSftpConnectionMutation,
  useUpdateSftpConnectionMutation,
  useDeleteSftpConnectionMutation,
  useAddSftpPublicKeyMutation,
  useRemoveSftpPublicKeyMutation,
  useGetBackendUserSftpConnectionsQuery,
  useLazyRevealSftpPasswordQuery,
  useLazyCheckSftpUsernameQuery,
  useGenerateEncryptedSftpKeyPairMutation,
} = sftpApi;
